<template>
    <div v-if="isLoading" class="d-flex align-items-center" style="gap: 5px">
        <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
        <span>{{ $t('components.datatable.index.loadingText') }}</span>
    </div>
    <div v-else class="row">
        <div class="col-xs-12 col-lg-6">
            <table class="table table-borderless table-bordered modal-table specs">
                <tbody>
                    <tr>
                        <td>{{ $t('views.inventory.modals.productDetail.supplyAndNotes.total_quantity') }}</td>
                        <td>
                            <template v-if="+supply.total_quantity > 0">
                                <Popper
                                    v-if="supply.total_quantity > 0"
                                    ref="tooltipButton"
                                    :options="{
                                        modifiers: [
                                            {
                                                name: 'flip',
                                                options: {
                                                    fallbackPlacements: ['top', 'right', 'bottom', 'left'],
                                                },
                                            },
                                            {
                                                name: 'preventOverflow',
                                                options: {
                                                    padding: { top: 10, bottom: 10 },
                                                },
                                                boundariesElement: 'viewport',
                                            },
                                        ],
                                    }"
                                    placement="bottom"
                                    v-bind="$attrs"
                                    arrow
                                    hover
                                    class="light-popper calculated-price-popper"
                                    style="border: none !important; padding: 0px !important; margin: 0px !important"
                                >
                                    <template #content>
                                        <div class="popper-table">
                                            <div class="d-flex" style="padding: 5px">
                                                <div class="d-flex flex-column">
                                                    <div class="c-item" style="padding: 3px 9px !important" v-for="detailKey of Object.keys(supply.quantity_details).sort()" :key="detailKey">
                                                        {{ $t('views.inventory.quantityDetails.' + detailKey) }}
                                                    </div>
                                                </div>
                                                <div class="d-flex flex-column">
                                                    <div class="c-item text-secondary" style="text-align: right; padding: 3px 9px !important; font-weight: 600" v-for="detailKey of Object.keys(supply.quantity_details).sort()" :key="detailKey">
                                                        {{ supply.quantity_details[detailKey] }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <span
                                        :class="+supply.total_quantity_fulfillable === 0 && supply.total_quantity > 0 && 'text-warning'"
                                        :style="+supply.total_quantity_fulfillable === 0 && supply.total_quantity > 0 ? 'text-decoration:underline' : ''"
                                        >{{ supply.total_quantity_fulfillable.toLocaleString() }}</span
                                    >
                                    <i class="bi bi-info-circle-fill opacity-05" style="margin-left: 2px" :class="+supply.total_quantity_fulfillable === 0 && supply.total_quantity > 0 && 'text-warning'"></i>
                                </Popper>
                            </template>
                            <span v-else class="text-center">0</span>
                        </td>
                        <td>{{ $t('views.inventory.modals.productDetail.supplyAndNotes.refund_count') }}</td>
                        <td>{{ supply.refund_count || '-' }}</td>
                    </tr>
                    <tr>
                        <td>{{ $t('views.inventory.modals.productDetail.supplyAndNotes.sales_count') }}</td>
                        <td>{{ supply.sales_count || '-' }}</td>
                        <td>{{ $t('views.inventory.modals.productDetail.supplyAndNotes.refund_rate') }}</td>
                        <td>
                            <template v-if="supply.refund_rate">%{{ formatNumber(supply.refund_rate) }}</template>
                            <template v-else>-</template>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div class="d-flex flex-column" style="margin-top: 20px">
                <span class="d-block" style="font-size: 14px; font-weight: 500; line-height: 21px; color: #00203f; margin-bottom: 10px">{{ $t('views.inventory.modals.productDetail.supplyAndNotes.notes.label') }}</span>
                <textarea v-model.trim="supply.notes" class="form-control" :placeholder="$t('views.inventory.modals.productDetail.supplyAndNotes.notes.placeholder')" style="min-height: 140px"></textarea>
            </div>

            <div class="d-flex justify-content-end" style="margin-top: 20px">
                <button type="button" class="btn btn-primary btn-sm" :disabled="isAddNoteButtonDisabled" @click="saveNote">{{ $t('views.inventory.modals.productDetail.supplyAndNotes.notes.submit') }}</button>
            </div>
        </div>
        <div class="col-xs-12 col-lg-6">
            <span class="d-block" style="font-size: 14px; font-weight: 500; line-height: 21px; color: #00203f; margin-bottom: 10px">{{ $t('views.inventory.modals.productDetail.supplyAndNotes.urls.label') }}</span>

            <div class="d-flex justify-content-between align-items-center" style="gap: 10px">
                <div class="input-group">
                    <span class="input-group-text" style="background-color: #fff !important"><i class="bi bi-link-45deg"></i></span>
                    <input v-model.trim="url" type="text" class="form-control" :placeholder="$t('views.inventory.modals.productDetail.supplyAndNotes.urls.placeholder')" />
                </div>
                <button type="button" class="btn btn-success btn-sm d-flex align-items-center" :disabled="isAddUrlButtonDisabled || !isValidURL(url)" style="width: 80px; gap: 5px" @click="addSupplyUrl">
                    <i class="bi bi-plus-lg"></i> <span>{{ $t('views.inventory.modals.productDetail.supplyAndNotes.urls.submit') }}</span>
                </button>
            </div>
            <span class="invalid-feedback" style="color: red !important">{{ url && !isValidURL(url) ? $t('actions.urlNotValid') : '' }}</span>

            <table v-if="supply.supplies" class="table modal-table" style="margin-top: 10px">
                <tbody>
                    <tr v-for="(item, index) in supply.supplies" :key="item.id">
                        <td class="filterName">
                            <div v-if="supply.editable" class="d-flex align-items-center" style="gap: 5px">
                                <input v-if="supply.editable" class="form-control" v-model="supply.value" style="max-height: 37.79px" />
                                <button class="btn btn-success btn-sm d-flex align-items-center justify-content-center" @click="editSupplyUrl(index, supply.value)" style="width: 37.79px; height: 37.79px"><i class="fa-solid fa-check"></i></button>
                            </div>
                            <a v-else :href="supply.value" target="_blank" class="text-primary text-decoration-underline" style="font-size: 14px; font-weight: 500; line-height: 21px">{{ supply.value }}</a>
                        </td>
                        <td style="max-width: 80px; padding-right: 0">
                            <div class="d-flex align-items-center justify-content-end" style="gap: 5px">
                                <button class="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-center" @click="deleteSupplyUrl(index)" style="width: 37.79px; height: 37.79px">
                                    <i class="fa-solid fa-trash"></i>
                                </button>
                                <template v-if="supply.editable">
                                    <button class="btn btn-primary btn-sm d-flex align-items-center justify-content-center" @click=";(supply.editable = false), (supply.value = supply.value_clone)" style="width: 37.79px; height: 37.79px">
                                        <i class="fa-solid fa-edit"></i>
                                    </button>
                                </template>
                                <template v-else>
                                    <button class="btn secondary-primary btn-sm d-flex align-items-center justify-content-center" @click="supply.editable = !supply.editable" style="width: 37.79px; height: 37.79px">
                                        <i class="fa-solid fa-edit"></i>
                                    </button>
                                </template>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import moment from 'moment'
import { countryInfo } from '@/utils/helper'

const countries = countryInfo()

export default {
    name: 'ProductDetailSupplyAndNotesModal',
    data() {
        return {
            countries,
            moment,
            url: '',
            isLoading: true,
        }
    },
    computed: {
        ...mapState('inventory', ['supply']),
        ...mapGetters('market', ['currentMarket']),
        getAmazonCountryURL() {
            return countries[this.currentMarket.short].domain
        },
        getCurrency() {
            return countries[this.currentMarket.short].symbol
        },
        isAddUrlButtonDisabled() {
            return !this.url
        },
        isAddNoteButtonDisabled() {
            return !this.supply.notes
        },
    },
    created() {
        this.getSupply().finally(() => {
            this.isLoading = false
        })
    },
    methods: {
        ...mapActions('inventory', ['getSupply', 'addSupply', 'editSupply', 'deleteSupply', 'addNote']),
        closeModal() {
            this.$emit('update:modelValue', false)
        },
        formatDate(date) {
            return moment(date).format('DD/MM/YYYY')
        },
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        addSupplyUrl() {
            this.addSupply({ url: this.url }).then(() => {
                this.url = ''
            })
        },
        editSupplyUrl(index, value) {
            this.editSupply({ index, payload: { url: value } })
        },
        deleteSupplyUrl(index, value) {
            if (confirm(this.$t('views.inventory.modals.productDetail.supplyAndNotes.deleteConfirm'))) {
                this.deleteSupply({ index, payload: { url: value } })
            }
        },
        saveNote() {
            this.addNote({ note: this.supply.notes })
        },
        isValidURL(url) {
            try {
                return Boolean(new URL(url))
            } catch (e) {
                return false
            }
        },
    },
    watch: {},
}
</script>

<style lang="scss">
.dp__theme_light {
    --dp-primary-color: #ffb53e;
}
.custom-form-control {
    max-height: 37px;
    font-size: 12px;
}
[id^='ProductDetail-'] {
    .modal-body {
        padding-top: 32px !important;
    }
    .close-button {
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;

        &:hover {
            background-color: #f8fafd;
        }
    }
    .modal-table {
        margin-bottom: 0 !important;
        border-radius: 5px;

        &.specs {
            td {
                width: 269px;
                height: 45px;

                &:nth-child(odd) {
                    border-right: 0 !important;
                }
            }
        }

        tbody {
            tr {
                td {
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 21px;
                    color: #677788;
                    height: 41px;
                }
                td:nth-child(odd) {
                    border-right: none;
                }
                td:nth-child(even) {
                    font-weight: 400;
                    color: #677788;
                    border-left: none;
                }
            }
        }

        .text-ellipsis {
            font-size: 11.9px;
            font-weight: 400;
            line-height: 17.9px;
            color: #677788;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;

            &.title {
                max-width: 250px;
            }

            &.category {
                max-width: 125px;
            }
        }
        .dp__input_wrap {
            width: auto;
            > input {
                padding-inline-end: 12px !important;
                font-size: 14px;
                border: 0.6px solid #e7eaf3;
                color: #677788;
                width: 125px !important;
            }
        }
    }

    .nav-segment {
        margin-right: 2rem;

        .nav-link {
            font-weight: 500;
            font-size: 14px;
        }
    }

    .multiselect {
        width: 140px !important;
        min-width: auto !important;

        &__placeholder {
            display: block !important;
            margin-bottom: 0 !important;
            padding-top: 0 !important;
        }

        &__tags {
            padding: 12px 40px 0 8px !important;
        }

        &__select {
            line-height: normal !important;
        }
    }

    .secondary-primary {
        color: #fff !important;
        background-color: var(--bs-gray-700) !important;

        &:hover {
            background-color: var(--fba-primary) !important;
        }
    }
    .filterName {
        min-width: 408px;
        max-width: 408px;
        padding-left: 0;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }
}
</style>
